// Shared SEO helpers: canonical site url, absolute urls, slugs and the paths of pages with slugs.

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://sustainability.co.id"
).replace(/\/+$/, "");

export const SITE_NAME = "Institute for Sustainability and Agility (ISA)";
export const SITE_SHORT_NAME = "ISA";
export const DEFAULT_OG_IMAGE = "/og-image.jpg";

// Page urls are lowercase: Netlify stores the prerendered files in lowercase and
// 301s any other casing to it, so canonical urls must match.
export const pagePath = (path = "/") =>
  /\.[a-z0-9]+$/i.test(path) ? path : path.toLowerCase();

// The key of `keys` that matches a url param regardless of case ("esg-audit" -> "ESG-Audit").
export const matchParam = (value, keys) =>
  keys.find((key) => key.toLowerCase() === String(value || "").toLowerCase()) || value;

// Turns "/blog/7", "static/media/x.png" or a full url into an absolute url.
// Spaces and other unsafe characters in API image urls are encoded.
export const absoluteUrl = (pathOrUrl = "/") => {
  if (!pathOrUrl) return SITE_URL;
  if (/^https?:\/\//i.test(pathOrUrl)) return encodeURI(decodeURI(pathOrUrl));
  const path = pagePath(pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`);
  return `${SITE_URL}${encodeURI(decodeURI(path))}`;
};

// "Pengantar ESG: Mengapa Penting?" -> "pengantar-esg-mengapa-penting"
export const slugify = (text = "", maxLength = 80) => {
  const slug = String(text)
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length <= maxLength) return slug;
  const cut = slug.slice(0, maxLength);
  return cut.slice(0, cut.lastIndexOf("-") > 20 ? cut.lastIndexOf("-") : maxLength);
};

// Title a blog post is shown with by default (English when there is one).
export const newsTitle = (news = {}) =>
  news.title_en || news.title_idn || news.title || "";

// Canonical blog post path: /blog/<id>/<slug>
export const blogPath = (news = {}) => {
  const slug = slugify(newsTitle(news));
  return slug ? `/blog/${news.id}/${slug}` : `/blog/${news.id}`;
};

// Canonical activity path: /activities/<id>/<slug>
export const activityPath = (activity = {}) => {
  const slug = slugify(activity.title);
  return slug ? `/activities/${activity.id}/${slug}` : `/activities/${activity.id}`;
};

// Plain text from an HTML string, collapsed and cut at a word boundary.
export const plainText = (html = "", maxLength = 160) => {
  let text = String(html || "")
    .replace(/<style[\s\S]*?<\/style>|<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ");
  if (typeof document !== "undefined") {
    const el = document.createElement("textarea");
    el.innerHTML = text;
    text = el.value;
  }
  text = text.replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength - 1);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 40 ? cut.lastIndexOf(" ") : cut.length)}…`;
};

// API dates ("2026-08-12T04:50:36.000Z") -> ISO string, or undefined when missing/invalid.
export const isoDate = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
};
